import { useEffect, useState } from "react";
import axios from "axios";
import { Link } from "react-router-dom";
import { Sparkles, ArrowRight, Clock3 } from "lucide-react";
import { whatsappLink } from "../data/content";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * Reads the first currently-active offer and renders a slim promotional strip.
 * Fails silently when nothing is active — never breaks the host page.
 */
export default function OfferBanner({ variant = "strip", onBook }) {
  const [offer, setOffer] = useState(null);

  useEffect(() => {
    axios
      .get(`${API}/offers/active`)
      .then((r) => setOffer(r.data.offers?.[0] || null))
      .catch(() => setOffer(null));
  }, []);

  if (!offer) return null;

  if (variant === "strip") {
    return (
      <aside className="offer-strip" data-testid="offer-strip">
        <Sparkles size={14} />
        <span className="offer-strip-label">{offer.kicker}</span>
        <span className="offer-strip-title">{offer.title}</span>
        <span className="offer-strip-price">
          ₹{offer.offer_price.toLocaleString("en-IN")}
          {offer.original_price > offer.offer_price && (
            <s>₹{offer.original_price.toLocaleString("en-IN")}</s>
          )}
        </span>
        {onBook ? (
          <button type="button" onClick={onBook} className="offer-strip-cta" data-testid="offer-strip-cta">
            {offer.cta_label || "Book this offer"} <ArrowRight size={13} />
          </button>
        ) : (
          <a
            href={whatsappLink(`Hello Zorba, I'd like to book the ${offer.title} (${offer.duration}, ₹${offer.offer_price}).`)}
            target="_blank"
            rel="noopener noreferrer"
            className="offer-strip-cta"
            data-testid="offer-strip-cta"
          >
            {offer.cta_label || "Book this offer"} <ArrowRight size={13} />
          </a>
        )}
      </aside>
    );
  }

  // "card" variant — richer presentation for section placement
  return (
    <section className="offer-card-section section-pad" data-testid="offer-card-section">
      <div className="offer-card">
        <div className="offer-card-media" style={{ backgroundImage: `url(${offer.image || "/images/zorba/signature.jpg"})` }}>
          <span className="offer-badge">{offer.discount_pct}% off</span>
        </div>
        <div className="offer-card-body">
          <p className="eyebrow"><Sparkles size={12} /> {offer.kicker}</p>
          <h2>{offer.title}</h2>
          <p className="muted">{offer.description}</p>
          <div className="offer-meta">
            <span><Clock3 size={13} /> {offer.duration}</span>
            <span className="offer-price-line">
              <strong>₹{offer.offer_price.toLocaleString("en-IN")}</strong>
              {offer.original_price > offer.offer_price && (
                <s>₹{offer.original_price.toLocaleString("en-IN")}</s>
              )}
            </span>
          </div>
          {offer.inclusions?.length > 0 && (
            <ul className="offer-inclusions">
              {offer.inclusions.slice(0, 6).map((i, idx) => (
                <li key={idx}>• {i}</li>
              ))}
            </ul>
          )}
          <div className="actions-row">
            {onBook ? (
              <button type="button" onClick={onBook} className="btn btn-primary" data-testid="offer-card-book">
                {offer.cta_label || "Book this offer"} <ArrowRight size={14} />
              </button>
            ) : (
              <a
                href={whatsappLink(`Hello Zorba, I'd like to book the ${offer.title} (${offer.duration}, ₹${offer.offer_price}).`)}
                target="_blank"
                rel="noopener noreferrer"
                className="btn btn-primary"
                data-testid="offer-card-book"
              >
                {offer.cta_label || "Book this offer"} <ArrowRight size={14} />
              </a>
            )}
            <Link to="/gift-vouchers/" className="btn btn-secondary" data-testid="offer-card-gift">
              Gift it instead
            </Link>
          </div>
        </div>
      </div>
    </section>
  );
}
